/**
 * Action pauses: every pause an action takes (sign-in, authorization wait, a field to fill, a
 * background-job wait) arrives as ONE information request with `form_type: "action_pause"` and a
 * `card_json` string that describes the whole card. The client renders from that card only; the
 * request id (`pause:<pause_id>`) is an opaque address for the answer.
 *
 * The answer always has the same shape, `{pause_id, button, value, values}`, serialised as the
 * information answer for the request id. `values` maps each field's id to its answer, and `value`
 * is the first field's answer, so a card with one field reads the same as it always has. A
 * background job answers some cards itself, with `button: "job_settled"` and the job's final
 * status as `value`.
 */

export const ACTION_PAUSE_FORM_TYPE = "action_pause";

/** The button id that sends ACTION.CANCEL rather than CONTINUE. */
export const PAUSE_CANCEL_BUTTON = "cancel";

/** The button the background-job sweep answers a wait card with. */
export const PAUSE_JOB_SETTLED_BUTTON = "job_settled";

/**
 * Not a button: the answer Cortex records when a `chat_reply` card is answered by a message typed
 * in the chat. Its `value` is the typed text, which is also the user message below the card.
 */
export const PAUSE_REPLY_BUTTON = "reply";

export type PauseTemplate = "confirm" | "text_input" | "wait";

/** The field id a legacy single-`field` card answers under. */
export const LEGACY_FIELD_ID = "value";

export interface PauseButton {
  id: string;
  label: string;
  primary?: boolean;
}

export type PauseFieldType = "text" | "email" | "choice";

/**
 * One input on a card. Every field is required. A `choice` is a strict pick from `options`, unless
 * the card has `other`, which adds a free-text "Other" to every choice.
 */
export interface PauseField {
  id: string;
  label: string;
  type: PauseFieldType | string;
  options: string[];
  placeholder?: string;
}

export interface PauseCardData {
  pause_id: number | string;
  template: PauseTemplate | string;
  title: string;
  body: string;
  pending_status?: string;
  buttons: PauseButton[];
  fields: PauseField[];
  wait_job_id?: number | string | null;
  stop_turn?: boolean;
  device_status?: { name: string; online: boolean | null; last_seen_at: number | null };
  notes?: Record<string, string>;
  /** Every `choice` field also offers "Other" with a text box; the typed text is the answer. */
  other: boolean;
  /**
   * A message typed in the chat while this card is open answers it rather than cancelling it.
   * With no buttons (and so no fields) the card is chat-only: the chat box is its only input.
   */
  chat_reply: boolean;
}

export interface AnsweredPause {
  button: string;
  value: string;
  /** Each field's answer by field id; empty for a button-only card or an answer stored before fields. */
  values?: Record<string, string>;
}

export interface PauseNote {
  text: string;
  declined: boolean;
}

interface InformationRequestLike {
  id?: string;
  question?: string;
  form_type?: string;
  card_json?: unknown;
}

interface HumanReviewLike {
  information_request?: InformationRequestLike[] | null;
  information_answers?: Record<string, unknown> | null;
}

const asObject = (raw: unknown): Record<string, unknown> | null => {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw as Record<string, unknown>;
  if (typeof raw !== "string" || !raw.trim()) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

const toButtons = (raw: unknown): PauseButton[] =>
  Array.isArray(raw)
    ? raw
        .map((entry) => asObject(entry))
        .filter((entry): entry is Record<string, unknown> => entry !== null && String(entry.id ?? "").trim() !== "")
        .map((entry) => ({
          id: String(entry.id),
          label: String(entry.label ?? entry.id),
          primary: entry.primary === true,
        }))
    : [];

const toNotes = (raw: unknown): Record<string, string> => {
  const notes = asObject(raw);
  if (!notes) return {};
  return Object.fromEntries(
    Object.entries(notes)
      .filter(([, value]) => typeof value === "string" && value.trim() !== "")
      .map(([key, value]) => [key, String(value)])
  );
};

const toField = (raw: Record<string, unknown>, fallbackId: string): PauseField => ({
  id: String(raw.id ?? "").trim() || fallbackId,
  label: String(raw.label ?? ""),
  type: String(raw.type ?? "text"),
  options: Array.isArray(raw.options) ? raw.options.map((option) => String(option)).filter((option) => option !== "") : [],
  placeholder: raw.placeholder != null ? String(raw.placeholder) : undefined,
});

/**
 * The card's fields. Cards stored before `fields` carry one `field` (or, on a `text_input`, none
 * at all); either is read as a single field with id "value", which is what its answer's `value`
 * always held.
 */
const toFields = (card: Record<string, unknown>): PauseField[] => {
  if (Array.isArray(card.fields)) {
    return card.fields
      .map((entry) => asObject(entry))
      .filter((entry): entry is Record<string, unknown> => entry !== null)
      .map((entry, index) => toField(entry, `q${index + 1}`));
  }
  const legacy = asObject(card.field);
  if (legacy) return [toField({ ...legacy, id: LEGACY_FIELD_ID }, LEGACY_FIELD_ID)];
  // A chat-only card has no fields by design: the chat box is its input.
  if (card.template === "text_input" && card.chat_reply !== true) return [toField({ id: LEGACY_FIELD_ID }, LEGACY_FIELD_ID)];
  return [];
};

/** Cards stored while `ask` was its own template are ordinary `text_input` cards. */
const toTemplate = (raw: unknown): string => {
  const template = String(raw ?? "confirm");
  return template === "ask" ? "text_input" : template;
};

/**
 * The card an information request carries, or null when it carries none.
 *
 * `card_json` crosses the wire as a string; an already-parsed object is accepted too. The body
 * falls back to the request's `question`, which Cortex fills with the same text.
 */
export function parsePauseCard(info: InformationRequestLike | null | undefined): PauseCardData | null {
  const card = asObject(info?.card_json);
  if (!card) return null;
  return {
    pause_id: (card.pause_id as number | string) ?? "",
    template: toTemplate(card.template),
    title: String(card.title ?? ""),
    body: String(card.body ?? info?.question ?? ""),
    ...(card.pending_status ? { pending_status: String(card.pending_status) } : {}),
    buttons: toButtons(card.buttons),
    fields: toFields(card),
    wait_job_id: (card.wait_job_id as number | string | null | undefined) ?? null,
    notes: toNotes(card.notes),
    other: card.other === true,
    chat_reply: card.chat_reply === true,
    ...(card.stop_turn === true ? { stop_turn: true, device_status: card.device_status as PauseCardData["device_status"] } : {}),
  };
}

/** Is every information request on this review an action pause? */
export function isActionPauseReview(humanReview: HumanReviewLike | null | undefined): boolean {
  const requests = humanReview?.information_request;
  if (!Array.isArray(requests) || requests.length === 0) return false;
  return requests.every((request) => request?.form_type === ACTION_PAUSE_FORM_TYPE);
}

/** The first action-pause request on the review, with its parsed card. */
export function actionPauseRequest(
  humanReview: HumanReviewLike | null | undefined
): { request: InformationRequestLike; card: PauseCardData } | null {
  if (!isActionPauseReview(humanReview)) return null;
  for (const request of humanReview?.information_request || []) {
    const card = parsePauseCard(request);
    if (card) return { request, card };
  }
  return null;
}

/**
 * The answer string sent back for a pressed button. `values` holds each field's answer by id;
 * `value` is the first field's answer (or, for a job, its status).
 */
export function pauseAnswer(
  card: Pick<PauseCardData, "pause_id">,
  button: string,
  value: string = "",
  values: Record<string, string> = {}
): string {
  return JSON.stringify({ pause_id: card.pause_id, button, value, values });
}

/** The id of the card's primary button, else its first non-cancel one: what submitting presses. */
export function primaryPauseButton(card: Pick<PauseCardData, "buttons">): string {
  const primary =
    card.buttons.find((button) => button.primary) || card.buttons.find((button) => button.id !== PAUSE_CANCEL_BUTTON);
  return primary?.id ?? "";
}

/** The answer a field was given, read from a stored answer. */
export function answeredFieldValue(answered: AnsweredPause | null, field: PauseField, index: number): string {
  // A chat reply answers the card as a whole; its text is the user message, not any field's value.
  if (!answered || answered.button === PAUSE_REPLY_BUTTON) return "";
  const stored = answered.values?.[field.id];
  if (stored != null) return stored;
  // An answer stored before `values` existed had only `value`, which was the one field's.
  return index === 0 ? answered.value : "";
}

/**
 * How the stored review was answered, read from `information_answers`.
 *
 * Read from what the server stored rather than from this tab, so a reloaded thread shows the
 * button that was actually pressed (or the job status the sweep answered with).
 */
export function answeredPause(humanReview: HumanReviewLike | null | undefined): AnsweredPause | null {
  const answers = humanReview?.information_answers;
  if (!answers || typeof answers !== "object") return null;
  const requestIds = new Set((humanReview?.information_request || []).map((request) => String(request?.id ?? "")));
  const entries = Object.entries(answers);
  // Prefer the answers addressed to this card's own requests; anything else is a fallback.
  const ordered = [
    ...entries.filter(([key]) => requestIds.has(key)),
    ...entries.filter(([key]) => !requestIds.has(key)),
  ];
  for (const [, raw] of ordered) {
    const parsed = asObject(raw);
    const button = parsed ? String(parsed.button ?? "").trim() : "";
    if (button) {
      const values = asObject(parsed?.values);
      return {
        button,
        value: String(parsed?.value ?? ""),
        values: values ? Object.fromEntries(Object.entries(values).map(([key, value]) => [key, String(value ?? "")])) : {},
      };
    }
  }
  return null;
}

/** The key into `card.notes` for an answer: the button, or `job:<status>` for a settled job. */
export function pauseNoteKey(answered: AnsweredPause): string {
  return answered.button === PAUSE_JOB_SETTLED_BUTTON ? `job:${answered.value}` : answered.button;
}

/**
 * The line under an answered card, or null. Red when the card was cancelled or the job it waited
 * on did not complete; green otherwise.
 */
export function pauseNote(card: Pick<PauseCardData, "notes">, answered: AnsweredPause | null): PauseNote | null {
  if (!answered) return null;
  const key = pauseNoteKey(answered);
  const text = card.notes?.[key];
  if (!text) return null;
  const declined =
    answered.button === PAUSE_CANCEL_BUTTON ||
    answered.button === "system_failed" ||
    answered.button === "system_cancelled" ||
    (answered.button === PAUSE_JOB_SETTLED_BUTTON && answered.value !== "completed");
  return { text, declined };
}

/** Statuses that mean the review has been answered. */
const ANSWERED_STATUSES = ["approved", "canceled", "scheduled"];

/**
 * The unanswered wait card holding this review open, or null.
 *
 * Used by the composer: a message typed against the open wait would supersede it.
 */
export function openWaitPause(
  humanReview: HumanReviewLike | null | undefined,
  humanReviewStatus?: string | null
): { request: InformationRequestLike; card: PauseCardData } | null {
  if (ANSWERED_STATUSES.includes(String(humanReviewStatus || ""))) return null;
  const pause = actionPauseRequest(humanReview);
  if (!pause || pause.card.template !== "wait") return null;
  return answeredPause(humanReview) === null ? pause : null;
}

/**
 * The unanswered `chat_reply` pause holding this review open, or null.
 *
 * Used by the composer: while it is open, a typed message answers it (Cortex does that), so the
 * composer stays open and says so.
 */
export function openChatReplyPause(
  humanReview: HumanReviewLike | null | undefined,
  humanReviewStatus?: string | null
): { request: InformationRequestLike; card: PauseCardData } | null {
  if (ANSWERED_STATUSES.includes(String(humanReviewStatus || ""))) return null;
  const pause = actionPauseRequest(humanReview);
  if (!pause || !pause.card.chat_reply) return null;
  return answeredPause(humanReview) === null ? pause : null;
}

/** Is the chat box this card's only input: `chat_reply` with no fields (at most a Cancel button)? */
export function isChatOnlyPause(card: Pick<PauseCardData, "chat_reply" | "fields"> | null | undefined): boolean {
  return card?.chat_reply === true && card.fields.length === 0;
}
export function updateWaitDeviceStatus(message: any, updates: Record<string, PauseCardData["device_status"]>): any {
  if (["approved", "canceled", "scheduled"].includes(message.human_review_status)) return message;
  const review = message.human_review;
  if (!review?.information_request) return message;
  if (answeredPause(review)) return message;
  const information_request = review.information_request.map((request: any) => {
    const card = asObject(request.card_json);
    const device = card?.stop_turn === true && updates[String(card.pause_id)];
    return device ? { ...request, card_json: JSON.stringify({ ...card, device_status: device }) } : request;
  });
  return { ...message, human_review: { ...review, information_request } };
}
